export type SubmissionHistoryItem = {
  id: number;
  language: string;
  status: string;
  error_message?: string | null;
  execution_time: number | null;
  memory_usage: number | null;
  passed_tests: number | null;
  total_tests: number | null;
  submitted_at: string;
  code: string;
};

export type SubmissionErrorSource = {
  status?: string;
  error?: string;
  message?: string;
  details?: string;
};

function firstNonEmptyLine(text: string): string {
  return text.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
}

function extractErrorHeadlineFromText(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const named = trimmed.match(/^([A-Za-z_][\w.]*Error|[A-Za-z_][\w.]*Exception)(?::|\s|$)/);
  if (named) return named[1];

  const lower = trimmed.toLowerCase();
  if (lower.includes('compilation failed') || lower.includes('compile error')) return 'Compilation Error';
  if (lower.includes('time limit') || lower.includes('timed out') || lower.includes('timeout exceeded')) {
    return 'Time Limit Exceeded';
  }
  if (lower.includes('syntaxerror') || lower.includes('syntax error')) return 'SyntaxError';
  if (lower.includes('nameerror')) return 'NameError';
  if (lower.includes('typeerror')) return 'TypeError';
  if (lower.includes('valueerror')) return 'ValueError';
  if (lower.includes('indentationerror')) return 'IndentationError';

  return null;
}

/** Нормализует status из ответа executor для сохранения в БД */
export function normalizeSubmissionStatus(data: SubmissionErrorSource): string {
  const raw = data.status?.trim();
  if (raw) return raw;
  const err = (data.error || data.message || '').toLowerCase();
  if (err.includes('compilation')) return 'Compilation Error';
  if (err.includes('time limit') || err.includes('timed out')) return 'Time Limit Exceeded';
  return 'Error';
}

/** Краткий текст ошибки для истории попыток */
export function buildSubmissionErrorMessage(
  data: SubmissionErrorSource,
  status: string
): string | null {
  if (status === 'Accepted' || status === 'Wrong Answer') return null;

  const parts = [data.error, data.message, data.details]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim());

  if (parts.length > 0) {
    const firstLine = firstNonEmptyLine(parts.join('\n'));
    const headline = extractErrorHeadlineFromText(firstLine);
    if (headline) return headline;
    if (firstLine.length <= 160) return firstLine;
    return `${firstLine.slice(0, 157)}...`;
  }

  if (status && status !== 'Error') return status;
  return null;
}

/** Текст ошибки в карточке истории (справа) */
export function formatSubmissionErrorDetail(
  item: SubmissionHistoryItem,
  kind: SubmissionStatusKind,
  t: (k: string) => string
): string {
  if (item.error_message?.trim()) return item.error_message.trim();

  const status = item.status?.trim() ?? '';
  if (kind === 'time-limit') return t('problemPage.submissionHistoryTimeLimit');
  if (kind === 'compilation-error') {
    if (status && !/^error$/i.test(status)) return status;
    return t('problemPage.submissionHistoryCompilationError');
  }
  if (kind === 'runtime-error') {
    if (status && !/^error$/i.test(status)) return status;
    return t('problemPage.submissionHistoryRuntimeError');
  }
  if (status === 'Invalid Output') return 'Invalid Output';
  if (status && status !== 'Error') return status;
  return t('problemPage.statusError');
}

export type SubmissionStatusKind =
  | 'accepted'
  | 'wrong-answer'
  | 'runtime-error'
  | 'time-limit'
  | 'compilation-error'
  | 'error';

const LANGUAGE_LABELS: Record<string, string> = {
  python: 'Python 3.11',
  python3: 'Python 3.11',
  javascript: 'JavaScript',
  java: 'Java',
  cpp: 'C++',
  'c++': 'C++',
  c: 'C',
  go: 'Go',
  rust: 'Rust',
  sql: 'SQL',
  mysql: 'MySQL',
  postgres: 'PostgreSQL',
  postgresql: 'PostgreSQL',
};

export function submissionStatusKind(status: string): SubmissionStatusKind {
  const s = status.trim().toLowerCase();
  if (s === 'accepted') return 'accepted';
  if (s.includes('wrong')) return 'wrong-answer';
  if (s.includes('time limit') || s === 'tle') return 'time-limit';
  if (s.includes('runtime')) return 'runtime-error';
  if (s.includes('compilation') || s.includes('compile')) return 'compilation-error';
  if (s.includes('invalid output')) return 'error';
  if (s.includes('forbidden')) return 'error';
  return 'error';
}

export function formatSubmissionLanguage(lang: string): string {
  const key = lang.trim().toLowerCase();
  return LANGUAGE_LABELS[key] ?? lang;
}

export function formatSubmissionDateTime(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function resolveSubmissionTestCounts(
  item: SubmissionHistoryItem,
  kind: SubmissionStatusKind,
  defaultTestCount: number
): { passed: number; total: number } {
  let passed = item.passed_tests ?? 0;
  let total = item.total_tests ?? 0;

  if (total <= 0 && defaultTestCount > 0) {
    total = defaultTestCount;
  }

  if (kind === 'accepted' && passed <= 0 && total > 0) {
    passed = total;
  }

  return { passed, total };
}

export function submissionStatusLabel(
  status: string,
  kind: SubmissionStatusKind,
  t: (k: string) => string
): string {
  if (kind === 'accepted') return t('problemPage.statusAccepted');
  if (kind === 'wrong-answer') return t('problemPage.statusWrongAnswer');
  if (kind === 'time-limit') return t('problemPage.submissionHistoryTimeLimit');
  if (kind === 'runtime-error') return t('problemPage.submissionHistoryRuntimeError');
  if (kind === 'compilation-error') return t('problemPage.submissionHistoryCompilationError');
  return t('problemPage.statusError');
}
